import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BookOpen, Search, Copy, Clock, History as HistoryIcon } from 'lucide-react';
import { listContents } from '../../utils/adminApi';

const TEAL = '#2EB4AC';

const ContentLibraryView = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listContents()
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) showToast?.('Failed to load content library', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showToast]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((c) =>
      !q || c.name.toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q)) || (c.body || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="content-library-view">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={20} style={{ color: TEAL }} />
        <h2 className="text-base font-semibold text-slate-700">Browse WhatsApp campaign content</h2>
        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">read only</span>
      </div>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, tag, or message body..."
          data-testid="content-library-search"
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      {loading ? (
        <p className="text-xs text-slate-400 text-center py-12">Loading content...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          <BookOpen size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">{items.length === 0 ? 'Library is empty' : 'No matches'}</p>
          <p className="text-[11px] text-slate-500 mt-1">{items.length === 0 ? 'Admins can add content from the Admin panel.' : 'Try a different search term.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="content-library-grid">
          {filtered.map((c) => (
            <ContentCard key={c.id} content={c} onOpen={() => setPreviewing(c)} />
          ))}
        </div>
      )}

      {previewing && <ContentPreviewModal content={previewing} onClose={() => setPreviewing(null)} showToast={showToast} />}
    </div>
  );
};

const ContentCard = ({ content, onOpen }) => {
  const preview = (content.body || '').slice(0, 140);
  return (
    <button
      onClick={onOpen}
      data-testid={`content-card-${content.name}`}
      className="text-left bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md rounded-xl p-3 transition-all"
    >
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-sm font-bold text-slate-900 line-clamp-1 flex-1 pr-2">{content.name}</h3>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">v{content.version}</span>
      </div>
      <div className="text-[10px] text-slate-400 mb-2">
        Updated {content.updated_at ? new Date(content.updated_at).toLocaleDateString() : '—'}
      </div>
      <p className="text-[11px] text-slate-600 whitespace-pre-wrap line-clamp-4 leading-relaxed">{preview || <span className="italic text-slate-400">No body.</span>}{(content.body || '').length > 140 && '...'}</p>
      {(content.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {content.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
          ))}
          {content.tags.length > 4 && <span className="text-[10px] text-slate-400">+{content.tags.length - 4}</span>}
        </div>
      )}
    </button>
  );
};

const ContentPreviewModal = ({ content, onClose, showToast }) => {
  const [showHistory, setShowHistory] = useState(false);
  const copyRef = useRef(null);
  const charCount = (content.body || '').length;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content.body || '');
      showToast?.('Copied to clipboard', 'success');
    } catch {
      if (copyRef.current) { copyRef.current.select(); document.execCommand('copy'); showToast?.('Copied', 'success'); }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="content-library-preview-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0 pr-3">
            <h3 className="text-base font-semibold text-slate-900 truncate">{content.name}</h3>
            <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
              <Clock size={10} /> Updated {content.updated_at ? new Date(content.updated_at).toLocaleString() : '—'}
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-semibold">v{content.version}</span>
              <span>{charCount} chars</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 text-sm">✕</button>
        </div>
        <div className="p-5 space-y-3">
          {(content.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {content.tags.map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
              ))}
            </div>
          )}
          <div className="relative">
            <div className="bg-[#e7ddd1] rounded-xl p-3"
              style={{ backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.2) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(255,255,255,0.15) 2%, transparent 0%)', backgroundSize: '100px 100px' }}>
              <div className="relative bg-white rounded-lg px-3 py-2 shadow-sm max-w-[90%] ml-auto" style={{ borderTopRightRadius: 4 }}>
                <pre className="text-[12px] text-slate-800 whitespace-pre-wrap font-sans leading-relaxed max-h-[280px] overflow-y-auto" data-testid="content-library-body">
                  {content.body || <span className="text-slate-400 italic">No content body.</span>}
                </pre>
                <div className="flex items-center justify-end gap-1 mt-1 text-[9px] text-slate-400">
                  <Clock size={8} /><span>now</span><span>✓✓</span>
                </div>
              </div>
            </div>
            <button onClick={onCopy} data-testid="content-library-copy-btn"
              className="absolute top-1 right-1 p-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 shadow-sm" title="Copy">
              <Copy size={11} className="text-slate-600" />
            </button>
          </div>
          <textarea ref={copyRef} value={content.body || ''} readOnly className="sr-only" />

          {(content.versions || []).length > 1 && (
            <div>
              <button onClick={() => setShowHistory((s) => !s)} data-testid="content-library-history-toggle"
                className="flex items-center gap-1 text-[10px] font-semibold text-teal-700 hover:underline">
                <HistoryIcon size={10} /> {showHistory ? 'Hide' : 'Show'} version history ({content.versions.length})
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                  {content.versions.slice().reverse().map((v) => (
                    <div key={v.version} className="border border-slate-200 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold text-slate-900">v{v.version}</span>
                        <span className="text-[9px] text-slate-400">{new Date(v.edited_at).toLocaleString()}</span>
                      </div>
                      <pre className="text-[11px] text-slate-600 whitespace-pre-wrap font-sans">{v.body}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button onClick={onCopy} data-testid="content-library-copy-large"
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: TEAL }}>
            <Copy size={12} /> Copy content
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentLibraryView;
